import React, { useEffect, useCallback, useState, memo, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import MouseIcon from '@mui/icons-material/Mouse';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { toggleCursorControl, updateCursorPosition } from '../store/presentationSlice';

interface CursorProps {
  isPresenter?: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const CursorControl: React.FC<CursorProps> = memo(({ isPresenter = false }) => {
  const dispatch = useDispatch();
  const { cursorControlEnabled, cursorPosition } = useSelector((state: RootState) => state.presentation);
  const [localPosition, setLocalPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMoving = useRef(false);
  const lastValidPosition = useRef({ x: 50, y: 50 });

  const handleToggle = useCallback(() => {
    dispatch(toggleCursorControl());
  }, [dispatch]);

  useEffect(() => {
    if (!isPresenter || !cursorControlEnabled) return;

    containerRef.current = document.querySelector('.presentation-container');
    if (!containerRef.current) return;

    let rafId: number | null = null;

    const updatePosition = (clientX: number, clientY: number) => {
      if (!containerRef.current) return;
      
      const bounds = containerRef.current.getBoundingClientRect();
      
      // Calculate relative position within container
      let x = ((clientX - bounds.left) / bounds.width) * 100;
      let y = ((clientY - bounds.top) / bounds.height) * 100;
      
      // Check if cursor is within bounds
      const inBounds = 
        clientX >= bounds.left && 
        clientX <= bounds.right && 
        clientY >= bounds.top && 
        clientY <= bounds.bottom;

      if (!inBounds) {
        // Snap to nearest edge
        x = clamp(x, 0, 100);
        y = clamp(y, 0, 100);
      } else {
        lastValidPosition.current = { x, y };
      }

      // Only update if position has changed significantly
      const dx = Math.abs(x - cursorPosition.x);
      const dy = Math.abs(y - cursorPosition.y);
      if (dx > 0.1 || dy > 0.1) {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          dispatch(updateCursorPosition({ x, y }));
          rafId = null;
        });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMoving.current) {
        isMoving.current = true;
        updatePosition(e.clientX, e.clientY);
        isMoving.current = false;
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isPresenter, cursorControlEnabled, dispatch, cursorPosition]);

  if (isPresenter) {
    return (
      <>
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
          <Tooltip title={cursorControlEnabled ? "Disable Cursor Control" : "Enable Cursor Control"}>
            <IconButton
              onClick={handleToggle}
              sx={{
                bgcolor: cursorControlEnabled ? 'primary.main' : 'grey.300',
                color: cursorControlEnabled ? 'white' : 'grey.700',
                '&:hover': {
                  bgcolor: cursorControlEnabled ? 'primary.dark' : 'grey.400',
                },
              }}
            >
              <MouseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </>
    );
  }

  if (!cursorControlEnabled) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 9999
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: `${clamp(cursorPosition.x, 0, 100)}%`,
          top: `${clamp(cursorPosition.y, 0, 100)}%`,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          transition: 'all 0.05s ease-out',
          width: { xs: 16, sm: 24 },
          height: { xs: 16, sm: 24 }
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 24 24"
          style={{
            filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.5))'
          }}
        >
          <circle cx="12" cy="12" r="8" fill="#ff0000" fillOpacity="0.4" />
          <circle cx="12" cy="12" r="4" fill="#ff0000" />
        </svg>
      </Box>
    </Box>
  );
});

export default CursorControl;
